import { Controller } from "@hotwired/stimulus"
import { acquire, renew, orphan, HEARTBEAT_MS } from "../lib/activity_lock"

// Connects to data-controller="pomodoro"
export default class extends Controller {
  static targets = ["workScreen", "breakScreen", "motivationScreen", "display", "savedTask", "taskInput", "startButton", "cancelButton", "finishButton", "motivationButton", "pomodoroCount"]
  static values = {
    workDuration: { type: Number, default: 1500 },
    breakDuration: { type: Number, default: 300 },
    task: {type: String, default: null},
    lightTimeId: { type: Number, default: null }
  }

  connect() {
    this.mode = "work"
    this.pomodoroCount = 0
    this.remainingTime = this.workDurationValue
    this.timerInterval = null
    // start() の再入ガード。await 中だけ true になる（#265）
    this.starting = false
    this.endedAt = null
    this.task = this.taskValue
    // 追記して修正できるように、マイページで入力した内容をやることの入力フォームに残しておく。
    this.taskInputTarget.value = this.task

    // ✅ 最初のタイマー開始時刻を保持
    this.firstStartedAt = null

    // 寝落ちチェックのための時刻
    this.lastActivityAt = null

    this.isMotivationOpen = false

    // ✅ 本番用の設定
    this.inactivityTimeout = 20 * 60 * 1000  // 20分
    this.checkInterval = 60 * 1000  // 1分ごとにチェック

    // ✅ テスト用の設定（動作確認時はこちらを使用）
    // this.inactivityTimeout = 60 * 1000  // 1分
    // this.checkInterval = 5 * 1000  // 5秒ごとにチェック

    // ✅ beforeunload イベントリスナーを追加
    this.boundBeforeUnloadHandler = this.handleBeforeUnload.bind(this)

    this.updateTimeDisplay()
    this.updatePomodoroCount()

    // 初期画面の表示でセット
    this.updateUI()

    this.startButtonTarget.classList.remove("hidden")

    // ✅ Turbo Drive は離脱時の（＝入れ替え済みの）DOM をキャッシュするため、
    //    ブラウザバック → フォワードの復元では hidden が入れ替わったまま戻ってくる。
    //    connect() は firstStartedAt を null に戻すので、出口も未スタートへ揃え直さないと
    //    「キャンセルが無く、終了するを押してもアラートが出るだけ」の行き止まりが復活する。
    //    startButton を毎回 remove しているのと同じ理由。
    this.updateExitButtons(false)
  }

  disconnect() {
    // ✅ コントローラーが破棄される時にイベントリスナーを削除
    this.removeBeforeUnloadListener()

    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }

    // ✅ release() は呼ばない。離脱（ブラウザバック / タブ閉じ）とフォームへの
    // 正常遷移をここで区別し始めると PR #88 と同じ破綻に戻る。
    clearInterval(this.lockHeartbeat)

    // Turbo Drive はページ内リンクの遷移で document を作り直さないため
    // pagehide は発火しない。Stimulus の disconnect() は Turbo 遷移でも
    // 確実に呼ばれるので、ここで自分のロックを orphan（期限を 5 秒に短縮）する。
    // 削除ではなく短縮なので、活動記録フォームへの正常遷移では遷移先の
    // activity-lock コントローラが 1 秒以内に acquire() し直して満了期限が戻る。
    orphan()
  }

  // タイトル更新（デバウンス付き）
  updateTaskDisplay() {
    this.task = this.taskInputTarget.value
    this.savedTaskTarget.textContent = this.task ? this.task : "フォームに入力して「更新する」をクリック！"
    // 前に入力した内容をinputタグに残すことに！
    // this.taskInputTarget.value = ''
  }

  async start() {
    // ✅ 二度押しで setInterval が二重に走るのを防ぐ（#265）。
    //    this.timerInterval は startTimer() まで、this.firstStartedAt は await の
    //    後まで設定されないため、この 2 つだけでは isPurificationCounting() の
    //    往復中に入ってきた 2 回目を弾けない。1 本目は this.timerInterval の
    //    上書きで参照を失い、二度と clearInterval できなくなる。
    //    表示ではなくフラグで閉じるのは、CSS が効かない状況でも守るためである。
    if (this.timerInterval || this.starting) return
    this.starting = true

    try {
      // ✅ 押した瞬間に消す。await の後まで画面が何も変わらないと「反応がない」と
      //    感じて押し直す動機になる。フラグとは別に、動機そのものを断つ。
      this.startButtonTarget.classList.add("hidden")

      await this.startSession()
    } catch (error) {
      // ✅ 想定外の失敗で「ボタンも無い・タイマーも動いていない」行き止まりにしない。
      //    押す前の状態へ戻し、もう一度試せるようにする。
      this.startButtonTarget.classList.remove("hidden")
      throw error
    } finally {
      this.starting = false
    }
  }

  // start() の本体。再入ガードは呼び出し元が持つ。
  async startSession() {
    // ✅ 最初のスタート時のみ記録
    if (this.firstStartedAt === null) {
      // ✅ 浄化タイマーが別タブで計測中なら、スタート押下の瞬間にサーバへ問い合わせて
      // 弾く。画面を開いた時点のガードだけでは、両タブを開いた後で浄化タイマーが
      // 後から開始されたケースを検知できない。
      const purificationCounting = await this.isPurificationCounting()

      // ✅ 往復の間に離脱していたら、この先の副作用を一切起こさない。
      //    「キャンセル」は Turbo Drive のリンク遷移なので document は作り直されず、
      //    disconnect() が済んだ後もこの Promise だけが再開する。そこで
      //    beforeunload やリースを張ると、片付ける者がいないまま残ってしまう
      //    （離脱警告が出続け、リースの heartbeat が回り続けて浄化タイマーに入れなくなる）。
      if (!this.element.isConnected) return

      if (purificationCounting) {
        location.replace("/mypage?locked=purification")
        return
      }

      this.firstStartedAt = new Date()
      // ✅ 離脱警告を有効化
      this.addBeforeUnloadListener()
      // ✅ ここから活動記録の登録完了までを「光の時間の活動中」とし、浄化タイマーを排他する
      this.startActivityLock()

      // ✅ 計測データが生まれたので出口を入れ替える。switchToWorkMode() は戻さないので
      //    休憩明けの作業画面でもスタートと並んだままになる。
      this.updateExitButtons(true)
    }

    // ✅ タイマー開始時は無操作チェックを停止
    this.stopInactivityCheck()

    if (this.mode === "work") {
      this.endedAt = this.getEndedAt(new Date(), this.workDurationValue)
    }

    this.startTimer()
  }

  // 浄化タイマーはサーバに状態を持つので、判定はサーバに問い合わせる
  // （localStorage では判定できない、非対称なガード）。通信に失敗した場合は
  // 排他制御のために本来の機能を止めない方針で、計測中ではないものとして扱う。
  async isPurificationCounting() {
    try {
      const response = await fetch("/purification_time", {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) return false

      const data = await response.json()
      return Boolean(data.counting)
    } catch {
      return false
    }
  }

  // ✅ 破壊的でない実装にする
  getEndedAt(startedAt, duration) {
    return new Date(startedAt.getTime() + duration * 1000)
  }

  startTimer() {
    this.timerInterval = setInterval(() => {
      this.remainingTime = Math.ceil((this.endedAt - new Date()) / 1000)
      this.updateTimeDisplay()

      if (this.remainingTime <= 0) {
        this.onTimerComplete()
      }
    }, 1000)
  }

  onTimerComplete() {
    clearInterval(this.timerInterval)
    this.timerInterval = null

    if (this.mode === "work") {
      this.pomodoroCount++
      this.updatePomodoroCount()
      this.switchToBreakMode()
    } else {
      this.switchToWorkMode()
    }
  }

  switchToWorkMode() {
    this.mode = "work"

    this.endedAt = this.getEndedAt(new Date(), this.workDurationValue)
    this.remainingTime = this.workDurationValue

    this.updateTimeDisplay()
    // 活動時間の計測画面へと遷移
    this.updateUI()
    this.startButtonTarget.classList.remove("hidden")
    // ✅ チャイム音を再生
    this.playSound('/sounds/notification.mp3')
    this.updateLastActivity()
    this.startInactivityCheck()
  }

  switchToBreakMode() {
    this.mode = "break"
    this.remainingTime = this.breakDurationValue
    this.endedAt = this.getEndedAt(new Date(), this.breakDurationValue)

    this.updateTimeDisplay()
    // 休憩時間の画面へと遷移
    this.updateUI()
    // ✅ チャイム音を再生
    this.playSound('/sounds/notification.mp3')
    this.startTimer()
  }

  updateTimeDisplay() {
    const minutes = Math.floor(this.remainingTime / 60)
    const seconds = this.remainingTime % 60

    const text = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    this.displayTargets.forEach(el => {
      el.textContent = text
    })
  }

  updatePomodoroCount() {
    this.pomodoroCountTargets.forEach(el => {
      el.textContent = this.pomodoroCount
    })
  }

  // ✅ 音声を再生するメソッド
  playSound(soundPath) {
    const audio = new Audio(soundPath)

    // 音量を設定（0.0 〜 1.0）
    audio.volume = 0.5

    // 再生
    audio.play().catch(() => {
      // ユーザーがまだページと対話していない場合に失敗することがある
    })
  }

  // ✅ beforeunload イベントハンドラー
  handleBeforeUnload(event) {
    event.preventDefault()
    // モダンブラウザでは戻り値は無視されるが、互換性のため設定
    event.returnValue = ''
    return ''
  }

  // ✅ イベントリスナーを追加
  addBeforeUnloadListener() {
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler)
  }

  // ✅ イベントリスナーを削除
  removeBeforeUnloadListener() {
    window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler)
  }

  // ✅ 無操作チェックの開始
  startInactivityCheck() {
    if (this.inactivityCheckInterval) return

    this.inactivityCheckInterval = setInterval(() => {
      this.checkInactivity()
    }, this.checkInterval)
  }

   // ✅ 無操作チェックの停止
  stopInactivityCheck() {
    if (this.inactivityCheckInterval) {
      clearInterval(this.inactivityCheckInterval)
      this.inactivityCheckInterval = null
    }
  }

  // ✅ 無操作チェック
  checkInactivity() {
    // タイマーが動いている時はチェックしない
    if (this.timerInterval) return

    if (!this.lastActivityAt) return

    const now = new Date()
    const timeSinceLastActivity = now - this.lastActivityAt

    // 5分以上操作がない場合
    if (timeSinceLastActivity >= this.inactivityTimeout) {
      // console.log("休憩後5分経過: 自動的に記録を保存します")

      this.stopInactivityCheck()
      this.removeBeforeUnloadListener()

      this.saveActivityRecordWithInactivityTimeout()
    }
  }

  // ✅ 最終操作時刻を更新
  updateLastActivity() {
    this.lastActivityAt = new Date()
  }

  // ✅ 無操作タイムアウト時の記録保存
  saveActivityRecordWithInactivityTimeout() {
    if (!this.firstStartedAt || !this.lastActivityAt) {
      alert("記録を保存できませんでした")
      return
    }

    // 最終操作時刻を終了時刻として使用
    const lastEndedAt = this.lastActivityAt

    const params = this.saveActivityRecord(lastEndedAt)

    // アラート文を表示
    alert(`操作がなかったため、自動的に記録を保存します。`)

    setTimeout(() => {
      location.replace(`/activity_records/new?${params.toString()}`)
    }, 300)
  }

  updateUI() {
    // 通常画面
    this.workScreenTarget.classList.toggle("hidden", this.mode !== "work" || this.isMotivationOpen)

    this.breakScreenTarget.classList.toggle("hidden", this.mode !== "break" || this.isMotivationOpen)

    // motivation画面
    this.motivationScreenTarget.classList.toggle("hidden", !this.isMotivationOpen)
  }

  toggleMotivation() {
    this.isMotivationOpen = !this.isMotivationOpen
    this.updateUI()
  }

  // 出口（キャンセル / 終了する / 集中できない…）の表示は「初回スタート済みか」だけで
  // 決まる。connect()（Turbo の復元を含む）と start() の 2 箇所から同じ真偽値で呼ぶ。
  // 逆向きの記述を 2 箇所に散らすと、片方を直し忘れて行き止まりが復活する。
  // モチベーションボタンは作業画面と休憩画面の 2 つ（同じ partial）に出るので複数形。
  updateExitButtons(started) {
    this.cancelButtonTarget.classList.toggle("hidden", started)
    this.finishButtonTarget.classList.toggle("hidden", !started)
    this.motivationButtonTargets.forEach(el => el.classList.toggle("hidden", !started))
  }

  // 「終了する」（作業画面・休憩画面）から呼ばれる。確認を取ってから終了する。
  finish() {
    // ✅ 確認ダイアログで迷っていた時間を活動時間に加算しないよう、押した瞬間を先に取る
    const lastEndedAt = new Date()

    // ✅ 確認は必ず副作用より前に通す。clearInterval の後ろに置くと、
    //    キャンセルした時にタイマーだけ止まって画面が残るという壊れ方をする。
    if (!window.confirm("終了してよろしいでしょうか？")) return

    this.completeSession(lastEndedAt)
  }

  // モチベーション画面「それでいいもん」から呼ばれる。あの画面自体が
  // 「本当にそれでいいですか？」と確認を兼ねているので、二重に確認しない。
  giveUp() {
    this.completeSession(new Date())
  }

  // 計測を止めて活動記録フォームへ遷移する。finish() / giveUp() の共通処理。
  completeSession(lastEndedAt) {
    // ✅ 2 つの入口（finish / giveUp）の合流点なので、ここで未スタートを弾く。
    //    hidden（display: none）により通常の操作では到達しないが、Tailwind の
    //    CSS が読み込まれない等でクラスが効かなくなれば到達しうる。
    //    外した場合は saveActivityRecord() の toISOString() が TypeError を投げ、
    //    クリックが無反応になる（total_duration が壊れたレコードが飛ぶわけではない）。
    //    表示制御という離れた場所の正しさに依存せず、ここで日本語のメッセージを返す。
    if (!this.firstStartedAt) {
      alert("スタートボタンを押してください")
      return
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }

    // ✅ タイマー終了時に離脱警告を無効化
    this.removeBeforeUnloadListener()

    // ✅ 最初のスタート時刻からの差分を計算
    const params = this.saveActivityRecord(lastEndedAt)
    // ✅ 確認フォーム画面に遷移
    location.replace(`/activity_records/new?${params.toString()}`)
  }

  // ✅ 光の時間の活動リースを取得し、以後 heartbeat で更新し続ける。
  // タイマー画面 → 活動記録フォームへの遷移は同一タブなので、遷移先の
  // activity-lock コントローラが同じリースをそのまま引き継いで更新する。
  startActivityLock() {
    acquire()
    this.lockHeartbeat = setInterval(() => renew(), HEARTBEAT_MS)
  }

  saveActivityRecord(lastEndedAt) {
    // ✅ 最初のスタート時刻からの差分を計算
    // const durationInSeconds = Math.floor((lastEndedAt - this.firstStartedAt) / 1000)
    const durationInMinutes = Math.floor((lastEndedAt - this.firstStartedAt) / 60000)

    // ✅ URLパラメータとして渡す
    const params = new URLSearchParams({
      'activity_record_form[task]': this.task,
      'activity_record_form[started_at]': this.firstStartedAt.toISOString(),
      'activity_record_form[ended_at]': lastEndedAt.toISOString(),
      'activity_record_form[total_duration]': durationInMinutes,
      'activity_record_form[light_time_id]': this.lightTimeIdValue
    })
    return params
  }
}
